import React, { type ElementType } from 'react';
import { twMerge } from 'tailwind-merge';
import type * as TF from 'type-fest';

import type { Breakpoint } from '#/ui/types';
import { calcSpacing, getResponsiveValue, isNil, styleVars } from '#/ui/utils/general';
import type { OverrideProps } from '#/ui/utils/types';
import Box from './box';

export type StackDirection = 'row' | 'row-reverse' | 'column' | 'column-reverse';

export type StackJustifyContent =
  | 'start'
  | 'center'
  | 'end'
  | 'space-between'
  | 'space-around'
  | 'space-evenly'
  | 'stretch';

export type StackAlignItems = 'start' | 'center' | 'end' | 'stretch';

export type StackAlignContent =
  | 'start'
  | 'center'
  | 'end'
  | 'space-between'
  | 'space-around'
  | 'space-evenly'
  | 'stretch';

export type StackJustifyItems = 'start' | 'center' | 'end' | 'stretch';

export interface StackOwnProps {
  /**
   * The spacing between the items in the stack.
   * @default 0
   */
  spacing?: number | Partial<Record<Breakpoint, number>>;
  /**
   * The direction of the stack.
   * @default 'column'
   */
  direction?: StackDirection | Partial<Record<Breakpoint, StackDirection>>;
  /**
   * Justify the items of the stack.
   */
  justifyItems?: StackJustifyItems | Partial<Record<Breakpoint, StackJustifyItems>>;
  /**
   * Justify the content of the stack.
   */
  justifyContent?: StackJustifyContent | Partial<Record<Breakpoint, StackJustifyContent>>;
  /**
   * Align the items of the stack.
   */
  alignItems?: StackAlignItems | Partial<Record<Breakpoint, StackAlignItems>>;
  /**
   * Align the content of the stack.
   */
  alignContent?: StackAlignContent | Partial<Record<Breakpoint, StackAlignContent>>;
  /**
   * A divider to place between the items.
   */
  divider?: React.ReactNode;
  /**
   * The content of the component.
   */
  children?: React.ReactNode;
}

export type StackTypeMap<
  AdditionalProps = TF.EmptyObject,
  RootComponent extends React.ElementType = 'div',
> = {
  props: AdditionalProps & StackOwnProps;
  defaultComponent: RootComponent;
};

export type StackProps<
  RootComponent extends React.ElementType = StackTypeMap['defaultComponent'],
  AdditionalProps = TF.EmptyObject,
  // @ts-expect-error
> = OverrideProps<StackTypeMap<AdditionalProps, RootComponent>, RootComponent> & {
  component?: React.ElementType;
};

function Stack<C extends ElementType = 'div'>(
  {
    component = 'div',
    spacing = 0,
    direction = 'column',
    children,
    divider,
    className,
    style,
    ...props
  }: StackProps<C>,
  ref?: React.ForwardedRef<C>
) {
  const responsiveDirection = React.useMemo(() => {
    return {
      base: typeof direction === 'string' ? direction : getResponsiveValue(direction, 'xs'),
      xs: getResponsiveValue(direction, 'xs'),
      sm: getResponsiveValue(direction, 'sm'),
      md: getResponsiveValue(direction, 'md'),
      lg: getResponsiveValue(direction, 'lg'),
      xl: getResponsiveValue(direction, 'xl'),
      '2xl': getResponsiveValue(direction, '2xl'),
    };
  }, [direction]);

  const directionClassnames = React.useMemo(() => {
    if (typeof direction === 'string') {
      return getDirectionClassname(direction, 'base');
    }

    return [
      !isNil(responsiveDirection['2xl']) &&
        getDirectionClassname(responsiveDirection['2xl'], '2xl'),
      !isNil(responsiveDirection.xl) && getDirectionClassname(responsiveDirection.xl, 'xl'),
      !isNil(responsiveDirection.lg) && getDirectionClassname(responsiveDirection.lg, 'lg'),
      !isNil(responsiveDirection.md) && getDirectionClassname(responsiveDirection.md, 'md'),
      !isNil(responsiveDirection.sm) && getDirectionClassname(responsiveDirection.sm, 'sm'),
      !isNil(responsiveDirection.xs) && getDirectionClassname(responsiveDirection.xs, 'xs'),
    ];
  }, [direction, responsiveDirection]);

  const responsiveSpacing = React.useMemo(() => {
    const base = typeof spacing === 'number' ? spacing : getResponsiveValue(spacing ?? null, 'xs');
    return {
      base,
      xs: getResponsiveValue(spacing ?? null, 'xs'),
      sm: getResponsiveValue(spacing ?? null, 'sm'),
      md: getResponsiveValue(spacing ?? null, 'md'),
      lg: getResponsiveValue(spacing ?? null, 'lg'),
      xl: getResponsiveValue(spacing ?? null, 'xl'),
      '2xl': getResponsiveValue(spacing ?? null, '2xl'),
    };
  }, [spacing]);

  const spacingClassnames = React.useMemo(() => {
    if (typeof spacing === 'number') {
      return 'gap-[var(--gooey-stack-spacing)]';
    }

    return [
      !isNil(responsiveSpacing['2xl']) && '2xl:gap-(--gooey-stack-spacing-2xl)',
      !isNil(responsiveSpacing.xl) && 'xl:gap-(--gooey-stack-spacing-xl)',
      !isNil(responsiveSpacing.lg) && 'lg:gap-(--gooey-stack-spacing-lg)',
      !isNil(responsiveSpacing.md) && 'md:gap-(--gooey-stack-spacing-md)',
      !isNil(responsiveSpacing.sm) && 'sm:gap-(--gooey-stack-spacing-sm)',
      !isNil(responsiveSpacing.xs) && 'gap-(--gooey-stack-spacing)',
    ];
  }, [spacing, responsiveSpacing]);

  const computedStyle = React.useMemo(() => {
    return {
      ...styleVars(
        {
          '--gooey-stack-spacing': responsiveSpacing.base ?? responsiveSpacing.xs,
          '--gooey-stack-spacing-sm': responsiveSpacing.sm,
          '--gooey-stack-spacing-md': responsiveSpacing.md,
          '--gooey-stack-spacing-lg': responsiveSpacing.lg,
          '--gooey-stack-spacing-xl': responsiveSpacing.xl,
        },
        (value) => calcSpacing(Number(value))
      ),
      ...style,
    };
  }, [responsiveSpacing, style]);

  const mergedClassnames = twMerge([
    'gooey-stack flex w-full',
    directionClassnames,
    spacingClassnames,
    getAlignContentClassnames(props.alignContent),
    getAlignItemsClassnames(props.alignItems),
    getJustifyItemsClassnames(props.justifyItems),
    getJustifyContent(props.justifyContent),
    className,
  ]);

  // If there's a divider, we need to handle it specially
  if (divider) {
    const childrenArray = React.Children.toArray(children);
    return (
      <Box
        {...props}
        component={component}
        ref={ref}
        className={mergedClassnames}
        style={computedStyle}
      >
        {childrenArray.map((child, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: allow
          <React.Fragment key={index}>
            {child}
            {index < childrenArray.length - 1 && divider}
          </React.Fragment>
        ))}
      </Box>
    );
  }

  return (
    <Box
      {...props}
      component={component}
      ref={ref}
      className={mergedClassnames}
      style={computedStyle}
    >
      {children}
    </Box>
  );
}
Stack.displayName = 'GooeyStack';

export default Stack;

/*
|------------------
| Internals
|------------------
*/

const alignContentMap = {
  start: {
    xs: 'content-start',
    sm: 'sm:content-start',
    md: 'md:content-start',
    lg: 'lg:content-start',
    xl: 'xl:content-start',
    '2xl': '2xl:content-start',
  },
  center: {
    xs: 'content-center',
    sm: 'sm:content-center',
    md: 'md:content-center',
    lg: 'lg:content-center',
    xl: 'xl:content-center',
    '2xl': '2xl:content-center',
  },
  end: {
    xs: 'content-end',
    sm: 'sm:content-end',
    md: 'md:content-end',
    lg: 'lg:content-end',
    xl: 'xl:content-end',
    '2xl': '2xl:content-end',
  },
  'space-between': {
    xs: 'content-between',
    sm: 'sm:content-between',
    md: 'md:content-between',
    lg: 'lg:content-between',
    xl: 'xl:content-between',
    '2xl': '2xl:content-between',
  },
  'space-around': {
    xs: 'content-around',
    sm: 'sm:content-around',
    md: 'md:content-around',
    lg: 'lg:content-around',
    xl: 'xl:content-around',
    '2xl': '2xl:content-around',
  },
  'space-evenly': {
    xs: 'content-evenly',
    sm: 'sm:content-evenly',
    md: 'md:content-evenly',
    lg: 'lg:content-evenly',
    xl: 'xl:content-evenly',
    '2xl': '2xl:content-evenly',
  },
  stretch: {
    xs: 'content-stretch',
    sm: 'sm:content-stretch',
    md: 'md:content-stretch',
    lg: 'lg:content-stretch',
    xl: 'xl:content-stretch',
    '2xl': '2xl:content-stretch',
  },
} satisfies Record<StackAlignContent, Record<Breakpoint, string>>;

function getAlignContentClassnames(alignContent: StackOwnProps['alignContent']) {
  if (typeof alignContent === 'string') {
    return alignContentMap[alignContent].xs;
  }

  if (isNil(alignContent)) {
    return;
  }

  return [
    !isNil(alignContent['2xl']) && alignContentMap[alignContent['2xl']]['2xl'],
    !isNil(alignContent.xl) && alignContentMap[alignContent.xl].xl,
    !isNil(alignContent.lg) && alignContentMap[alignContent.lg].lg,
    !isNil(alignContent.md) && alignContentMap[alignContent.md].md,
    !isNil(alignContent.sm) && alignContentMap[alignContent.sm].sm,
    !isNil(alignContent.xs) && alignContentMap[alignContent.xs].xs,
  ];
}

const alignItemsMap = {
  start: {
    xs: 'items-start',
    sm: 'sm:items-start',
    md: 'md:items-start',
    lg: 'lg:items-start',
    xl: 'xl:items-start',
    '2xl': '2xl:items-start',
  },
  center: {
    xs: 'items-center',
    sm: 'sm:items-center',
    md: 'md:items-center',
    lg: 'lg:items-center',
    xl: 'xl:items-center',
    '2xl': '2xl:items-center',
  },
  end: {
    xs: 'items-end',
    sm: 'sm:items-end',
    md: 'md:items-end',
    lg: 'lg:items-end',
    xl: 'xl:items-end',
    '2xl': '2xl:items-end',
  },
  stretch: {
    xs: 'items-stretch',
    sm: 'sm:items-stretch',
    md: 'md:items-stretch',
    lg: 'lg:items-stretch',
    xl: 'xl:items-stretch',
    '2xl': '2xl:items-stretch',
  },
} satisfies Record<StackAlignItems, Record<Breakpoint, string>>;

function getAlignItemsClassnames(alignItems: StackOwnProps['alignItems']) {
  if (typeof alignItems === 'string') {
    return alignItemsMap[alignItems].xs;
  }

  if (isNil(alignItems)) {
    return;
  }

  return [
    !isNil(alignItems['2xl']) && alignItemsMap[alignItems['2xl']]['2xl'],
    !isNil(alignItems.xl) && alignItemsMap[alignItems.xl].xl,
    !isNil(alignItems.lg) && alignItemsMap[alignItems.lg].lg,
    !isNil(alignItems.md) && alignItemsMap[alignItems.md].md,
    !isNil(alignItems.sm) && alignItemsMap[alignItems.sm].sm,
    !isNil(alignItems.xs) && alignItemsMap[alignItems.xs].xs,
  ];
}

const justifyItemsMap = {
  start: {
    xs: 'justify-items-start',
    sm: 'sm:justify-items-start',
    md: 'md:justify-items-start',
    lg: 'lg:justify-items-start',
    xl: 'xl:justify-items-start',
    '2xl': '2xl:justify-items-start',
  },
  center: {
    xs: 'justify-items-center',
    sm: 'sm:justify-items-center',
    md: 'md:justify-items-center',
    lg: 'lg:justify-items-center',
    xl: 'xl:justify-items-center',
    '2xl': '2xl:justify-items-center',
  },
  end: {
    xs: 'justify-items-end',
    sm: 'sm:justify-items-end',
    md: 'md:justify-items-end',
    lg: 'lg:justify-items-end',
    xl: 'xl:justify-items-end',
    '2xl': '2xl:justify-items-end',
  },
  stretch: {
    xs: 'justify-items-stretch',
    sm: 'sm:justify-items-stretch',
    md: 'md:justify-items-stretch',
    lg: 'lg:justify-items-stretch',
    xl: 'xl:justify-items-stretch',
    '2xl': '2xl:justify-items-stretch',
  },
} satisfies Record<StackJustifyItems, Record<Breakpoint, string>>;

function getJustifyItemsClassnames(justifyItems: StackOwnProps['justifyItems']) {
  if (typeof justifyItems === 'string') {
    return justifyItemsMap[justifyItems].xs;
  }

  if (isNil(justifyItems)) {
    return;
  }

  return [
    !isNil(justifyItems['2xl']) && justifyItemsMap[justifyItems['2xl']]['2xl'],
    !isNil(justifyItems.xl) && justifyItemsMap[justifyItems.xl].xl,
    !isNil(justifyItems.lg) && justifyItemsMap[justifyItems.lg].lg,
    !isNil(justifyItems.md) && justifyItemsMap[justifyItems.md].md,
    !isNil(justifyItems.sm) && justifyItemsMap[justifyItems.sm].sm,
    !isNil(justifyItems.xs) && justifyItemsMap[justifyItems.xs].xs,
  ];
}

const justifyContentMap = {
  start: {
    xs: 'justify-start',
    sm: 'sm:justify-start',
    md: 'md:justify-start',
    lg: 'lg:justify-start',
    xl: 'xl:justify-start',
    '2xl': '2xl:justify-start',
  },
  center: {
    xs: 'justify-center',
    sm: 'sm:justify-center',
    md: 'md:justify-center',
    lg: 'lg:justify-center',
    xl: 'xl:justify-center',
    '2xl': '2xl:justify-center',
  },
  end: {
    xs: 'justify-end',
    sm: 'sm:justify-end',
    md: 'md:justify-end',
    lg: 'lg:justify-end',
    xl: 'xl:justify-end',
    '2xl': '2xl:justify-end',
  },
  'space-between': {
    xs: 'justify-between',
    sm: 'sm:justify-between',
    md: 'md:justify-between',
    lg: 'lg:justify-between',
    xl: 'xl:justify-between',
    '2xl': '2xl:justify-between',
  },
  'space-around': {
    xs: 'justify-around',
    sm: 'sm:justify-around',
    md: 'md:justify-around',
    lg: 'lg:justify-around',
    xl: 'xl:justify-around',
    '2xl': '2xl:justify-around',
  },
  'space-evenly': {
    xs: 'justify-evenly',
    sm: 'sm:justify-evenly',
    md: 'md:justify-evenly',
    lg: 'lg:justify-evenly',
    xl: 'xl:justify-evenly',
    '2xl': '2xl:justify-evenly',
  },
  stretch: {
    xs: 'justify-stretch',
    sm: 'sm:justify-stretch',
    md: 'md:justify-stretch',
    lg: 'lg:justify-stretch',
    xl: 'xl:justify-stretch',
    '2xl': '2xl:justify-stretch',
  },
} satisfies Record<StackJustifyContent, Record<Breakpoint, string>>;

function getJustifyContent(justifyContent: StackOwnProps['justifyContent']) {
  if (typeof justifyContent === 'string') {
    return justifyContentMap[justifyContent].xs;
  }

  if (isNil(justifyContent)) {
    return;
  }

  return [
    !isNil(justifyContent['2xl']) && justifyContentMap[justifyContent['2xl']]['2xl'],
    !isNil(justifyContent.xl) && justifyContentMap[justifyContent.xl].xl,
    !isNil(justifyContent.lg) && justifyContentMap[justifyContent.lg].lg,
    !isNil(justifyContent.md) && justifyContentMap[justifyContent.md].md,
    !isNil(justifyContent.sm) && justifyContentMap[justifyContent.sm].sm,
    !isNil(justifyContent.xs) && justifyContentMap[justifyContent.xs].xs,
  ];
}

const directionMap = {
  row: {
    base: 'flex-row',
    xs: 'flex-row',
    sm: 'sm:flex-row',
    md: 'md:flex-row',
    lg: 'lg:flex-row',
    xl: 'xl:flex-row',
    '2xl': '2xl:flex-row',
  },
  column: {
    base: 'flex-col',
    xs: 'flex-col',
    sm: 'sm:flex-col',
    md: 'md:flex-col',
    lg: 'lg:flex-col',
    xl: 'xl:flex-col',
    '2xl': '2xl:flex-col',
  },
  'column-reverse': {
    base: 'flex-col-reverse',
    xs: 'flex-col-reverse',
    sm: 'sm:flex-col-reverse',
    md: 'md:flex-col-reverse',
    lg: 'lg:flex-col-reverse',
    xl: 'xl:flex-col-reverse',
    '2xl': '2xl:flex-col-reverse',
  },
  'row-reverse': {
    base: 'flex-row-reverse',
    xs: 'flex-row-reverse',
    sm: 'sm:flex-row-reverse',
    md: 'md:flex-row-reverse',
    lg: 'lg:flex-row-reverse',
    xl: 'xl:flex-row-reverse',
    '2xl': '2xl:flex-row-reverse',
  },
} satisfies Record<StackDirection, Record<Breakpoint | 'base', string>>;

function getDirectionClassname(direction: StackDirection, breakpoint: Breakpoint | 'base' | null) {
  return directionMap[direction][breakpoint ?? 'base'];
}
